import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

import { AuthProvider, useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/query-client";
import { ToastProvider } from "@/components/toast";
import { PaywallProvider } from "@/components/paywall";
import { ErrorBoundary } from "@/components/error-boundary";
import { Loading } from "@/components/ui";
import { MemberLayout } from "@/components/layouts/member-layout";
import { AdminLayout } from "@/components/layouts/admin-layout";

import Landing from "@/pages/landing";
import Login from "@/pages/login";
import Register from "@/pages/register";
import Verify from "@/pages/verify";
import Legal from "@/pages/legal";
import Onboarding from "@/pages/onboarding";
import Discover from "@/pages/discover";
import Requests from "@/pages/requests";
import Connections from "@/pages/connections";
import Notifications from "@/pages/notifications";
import Profile from "@/pages/profile";
import MemberDetail from "@/pages/member-detail";
import Saved from "@/pages/saved";
import PrivacySettings from "@/pages/settings-privacy";
import BlockedMembers from "@/pages/settings-blocked";
import AccountSecurity from "@/pages/settings-security";
import Verification from "@/pages/verification";
import Membership from "@/pages/membership";
import ReviewQueue from "@/pages/review/queue";
import ReviewDetail from "@/pages/review/detail";
import AdminHome from "@/pages/admin/home";
import AdminStaff from "@/pages/admin/staff";
import AdminMembers from "@/pages/admin/members";
import AdminMemberDetail from "@/pages/admin/member-detail";
import AdminReports from "@/pages/admin/reports";
import AdminReportDetail from "@/pages/admin/report-detail";
import AdminConfig from "@/pages/admin/config";
import AdminPayments from "@/pages/admin/payments";
import AdminAnalytics from "@/pages/admin/analytics";
import AdminContent from "@/pages/admin/content";
import AdminAudit from "@/pages/admin/audit";
import AdminGroups from "@/pages/admin/groups";
import AdminDeletionRequests from "@/pages/admin/deletion-requests";
import DeleteAccount from "@/pages/delete-account";
import PaymentCallback from "@/pages/payment-callback";

function Index() {
  const { status, user } = useAuth();
  const [params] = useSearchParams();
  const invite = params.get("invite");
  if (status === "loading") return <Loading />;
  // Community invite links: https://www.lovenet.chat/?invite=CODE → signup with the code prefilled
  if (status === "guest" || !user) return <Navigate to={invite ? `/register?invite=${encodeURIComponent(invite)}` : "/landing"} replace />;
  if (user.roles?.length) return <Navigate to="/admin" replace />;
  if (!user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return <Navigate to="/discover" replace />;
}

const MEMBER_ROUTES = [
  ["discover", Discover], ["requests", Requests], ["connections", Connections], ["notifications", Notifications],
  ["profile", Profile], ["member/:id", MemberDetail], ["saved", Saved], ["settings/privacy", PrivacySettings],
  ["settings/blocked", BlockedMembers], ["settings/security", AccountSecurity], ["verification", Verification], ["membership", Membership],
];

const ADMIN_ROUTES = [
  ["staff", AdminStaff], ["members", AdminMembers], ["member/:id", AdminMemberDetail], ["reports", AdminReports],
  ["report/:id", AdminReportDetail], ["config", AdminConfig], ["payments", AdminPayments], ["analytics", AdminAnalytics],
  ["content", AdminContent], ["audit", AdminAudit], ["groups", AdminGroups], ["deletion-requests", AdminDeletionRequests],
];

// Public, no-login policy pages (store listings link here). Content comes from the backend legal pages.
const POLICY_ROUTES = [["terms", "terms"], ["privacy", "privacy"], ["child-safety", "child_safety"], ["safety", "safety"], ["guidelines", "guidelines"]];

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <PaywallProvider>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/landing" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/verify" element={<Verify />} />
                  <Route path="/legal/:doc" element={<Legal />} />
                  {POLICY_ROUTES.map(([p, k]) => <Route key={p} path={`/${p}`} element={<Legal key={k} docKey={k} />} />)}
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route path="/payment/callback" element={<PaymentCallback />} />
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route element={<MemberLayout />}>
                    {MEMBER_ROUTES.map(([p, C]) => <Route key={p} path={`/${p}`} element={<ErrorBoundary><C /></ErrorBoundary>} />)}
                  </Route>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin" element={<AdminHome />} />
                    {ADMIN_ROUTES.map(([p, C]) => <Route key={p} path={`/admin/${p}`} element={<ErrorBoundary><C /></ErrorBoundary>} />)}
                    <Route path="/review" element={<ReviewQueue />} />
                    <Route path="/review/:id" element={<ErrorBoundary><ReviewDetail /></ErrorBoundary>} />
                  </Route>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </PaywallProvider>
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
